import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { X, Calendar, ChevronDown } from 'lucide-react';
import { BASE_URL } from '../Api/Api';
import classes from './AddBankAccountModal.module.css';

const initialFormState = {
    accountName: '',
    accountCode: '',
    shortName: '',
    currency: 'NGN',
    ledgerBalance: '',
    ledgerBalanceAsAt: '',
    statementBalance: '',
    statementBalanceAsAt: '',
    keywordReference: '',
};

const generateUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

const toApiDate = (value) => {
    if (!value) return '';

    const match = value.match(/^\d{2}\/\d{2}\/\d{4}$/);
    if (match) {
        const [day, month, year] = value.split('/');
        return `${year}-${month}-${day}`;
    }

    return value;
};

const getUserIdFromToken = (token) => {
    if (!token || typeof token !== 'string') return null;

    const userIdMatch = token.match(/^\s*(\d+)\s*\|/);
    if (!userIdMatch) return null;

    const parsedId = Number(userIdMatch[1]);
    return Number.isFinite(parsedId) ? parsedId : null;
};

export default function AddBankAccountModal({ isOpen, onClose, onAccountAdded }) {
    const [form, setForm] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [currencyOptions, setCurrencyOptions] = useState([]);

    const companyId = localStorage.getItem('companyId') || localStorage.getItem('customerId') || '';
    const createdBy = localStorage.getItem('userId') || localStorage.getItem('customerId') || '';

    React.useEffect(() => {
        if (!isOpen) return;

        const fetchCurrencies = async () => {
            const token = localStorage.getItem('userToken');

            try {
                const response = await axios.get(`${BASE_URL}/fetch-all-currencies`, {
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        'Content-Type': 'application/json',
                    },
                });

                const currencies = response?.data?.data || response?.data?.currencies || response?.data || [];
                const normalized = Array.isArray(currencies)
                    ? currencies.map((currency) => {
                        if (typeof currency === 'string') return { code: currency, name: currency };
                        return {
                            code: currency.code || currency.currency || currency.iso_code || currency.value || '',
                            name: currency.name || currency.label || currency.currency || currency.code || currency.iso_code || '',
                        };
                    }).filter((item) => item.code)
                    : [];

                if (normalized.length > 0) {
                    setCurrencyOptions(normalized);
                    setForm((prev) => ({
                        ...prev,
                        currency: String(prev.currency || normalized[0].code).toUpperCase(),
                    }));
                } else {
                    setCurrencyOptions([{ code: 'NGN', name: 'Nigerian Naira' }]);
                }
            } catch (error) {
                console.error('Fetch currencies failed:', error?.response?.data || error);
                setCurrencyOptions([{ code: 'NGN', name: 'Nigerian Naira' }]);
            }
        };

        fetchCurrencies();
    }, [isOpen]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const resetForm = () => setForm(initialFormState);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError('');

        const accountCode = form.accountCode.trim();
        const shortName = form.shortName.trim();
        const description = form.accountName.trim() || shortName;

        if (!accountCode || !shortName || !form.ledgerBalance || !form.ledgerBalanceAsAt || !form.statementBalance || !form.statementBalanceAsAt) {
            setSubmitError('Please complete all required account information and opening balances.');
            return;
        }

        const token = localStorage.getItem('userToken');
        const tokenUserId = getUserIdFromToken(token);
        const effectiveCreatedBy = tokenUserId ?? (Number(createdBy) || createdBy || null);

        const payload = {
            uuid: generateUuid(),
            company_id: companyId || null,
            account_code: accountCode,
            short_name: shortName,
            description,
            currency: String(form.currency || 'NGN').toUpperCase(),
            keyword_reference: form.keywordReference.trim() || accountCode,
            ledger_balance: Number(form.ledgerBalance),
            ledger_balance_as_at: toApiDate(form.ledgerBalanceAsAt),
            statement_balance: Number(form.statementBalance),
            statement_balance_as_at: toApiDate(form.statementBalanceAsAt),
            status: 'active',
            created_by: effectiveCreatedBy,
        };

        console.log('Create account payload:', payload);
        setIsSubmitting(true);

        try {
            const response = await axios.post(`${BASE_URL}/reconciliation/create_account`, payload, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    'Content-Type': 'application/json',
                },
            });

            console.log('Create account response:', response?.data);

            Swal.fire({
                icon: 'success',
                title: 'Account created',
                text: 'The bank account has been created successfully.',
                confirmButtonColor: '#0f766e',
            });

            resetForm();
            if (typeof onAccountAdded === 'function') {
                onAccountAdded();
            }
            onClose();
        } catch (error) {
            console.error('Create account failed:', error?.response?.data || error);
            const message = error?.response?.data?.message || error?.message || 'Failed to create bank account.';
            const errorText = typeof message === 'string' ? message : JSON.stringify(message);
            setSubmitError(errorText);
            Swal.fire({
                icon: 'error',
                title: 'Create account failed',
                text: errorText,
                confirmButtonColor: '#dc2626',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={classes.overlay} onClick={onClose}>
            <div
                className={classes.modalContainer}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-bank-account-title"
            >
                <div className={classes.header}>
                    <h2 id="add-bank-account-title">Add Bank Account</h2>
                    <button
                        type="button"
                        className={classes.closeButton}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={22} strokeWidth={2.2} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {submitError && (
                        <div className={classes.formError} role="alert">
                            {submitError}
                        </div>
                    )}

                    <section className={classes.section}>
                        <h3 className={classes.sectionTitle}>1. Account Information</h3>

                        <div className={classes.grid}>
                            <div className={classes.inputGroup}>
                                <label className={classes.label}>
                                    Account Name<span className={classes.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="accountName"
                                    className={classes.input}
                                    placeholder="Enter account name"
                                    value={form.accountName}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.inputGroup}>
                                <label className={classes.label}>
                                    Account Code<span className={classes.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="accountCode"
                                    className={classes.input}
                                    placeholder="Enter account code"
                                    value={form.accountCode}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.inputGroup}>
                                <label className={classes.label}>
                                    Short Name<span className={classes.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="shortName"
                                    className={classes.input}
                                    placeholder="Enter short name for this account"
                                    value={form.shortName}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.inputGroup}>
                                <label className={classes.label}>
                                    Currency<span className={classes.required}>*</span>
                                </label>
                                <div className={classes.inputWrapper}>
                                    <select
                                        name="currency"
                                        className={`${classes.input} ${classes.select}`}
                                        value={form.currency}
                                        onChange={handleChange}
                                    >
                                        {currencyOptions.length === 0 ? (
                                            <option value="NGN">NGN - Nigerian Naira</option>
                                        ) : (
                                            currencyOptions.map((currency) => (
                                                <option key={currency.code} value={currency.code}>
                                                    {currency.code} - {currency.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown size={18} className={classes.suffixIcon} />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className={classes.section}>
                        <div className={classes.openingBalanceHeader}>
                            <h3 className={classes.sectionTitle}>2. Opening Balance Information</h3>
                            <p
                                className={classes.sectionSubtitle}
                                style={{ marginTop: '-12px', marginBottom: '24px' }}
                            >
                                Provide opening balances at the date below
                            </p>
                        </div>

                        <div className={classes.balanceRow}>
                            <div className={classes.statementColumn}>
                                <div className={classes.statementColumnTitle}>
                                    Balance as per Ledger
                                </div>
                                <div className={classes.statementGrid}>
                                    <div className={classes.inputGroup}>
                                        <label className={classes.label}>
                                            Balance<span className={classes.required}>*</span>
                                        </label>
                                        <div className={classes.inputWrapper}>
                                            <span className={classes.prefixIcon}>₦</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="ledgerBalance"
                                                className={`${classes.input} ${classes.withPrefix}`}
                                                placeholder="Enter Amount"
                                                value={form.ledgerBalance}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <span className={classes.subtext}>Opening balance</span>
                                    </div>

                                    <div className={classes.inputGroup}>
                                        <label className={classes.label}>
                                            As at Date<span className={classes.required}>*</span>
                                        </label>
                                        <div className={classes.inputWrapper}>
                                            <input
                                                type="date"
                                                name="ledgerBalanceAsAt"
                                                className={`${classes.input} ${classes.withSuffix}`}
                                                value={form.ledgerBalanceAsAt}
                                                onChange={handleChange}
                                            />
                                            <Calendar size={18} className={classes.suffixIcon} />
                                        </div>
                                        <span className={classes.subtext}>Date of cashbook balance</span>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.statementColumn}>
                                <div className={classes.statementColumnTitle}>
                                    Balance as per Statement
                                </div>
                                <div className={classes.statementGrid}>
                                    <div className={classes.inputGroup}>
                                        <label className={classes.label}>
                                            Balance <span className={classes.required}>*</span>
                                        </label>
                                        <div className={classes.inputWrapper}>
                                            <span className={classes.prefixIcon}>₦</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="statementBalance"
                                                className={`${classes.input} ${classes.withPrefix}`}
                                                placeholder="Enter Amount"
                                                value={form.statementBalance}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <span className={classes.subtext}>Opening balance</span>
                                    </div>

                                    <div className={classes.inputGroup}>
                                        <label className={classes.label}>
                                            As at Date<span className={classes.required}>*</span>
                                        </label>
                                        <div className={classes.inputWrapper}>
                                            <input
                                                type="date"
                                                name="statementBalanceAsAt"
                                                className={`${classes.input} ${classes.withSuffix}`}
                                                value={form.statementBalanceAsAt}
                                                onChange={handleChange}
                                            />
                                            <Calendar size={18} className={classes.suffixIcon} />
                                        </div>
                                        <span className={classes.subtext}>Date of statement balance</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={classes.infoBox}>
                            <span className={classes.infoBadge} aria-hidden="true">
                                i
                            </span>
                            <span>
                                The opening balances will be used as the starting point for
                                reconciliation.
                            </span>
                        </div>
                    </section>

                    <div className={classes.footer}>
                        <button type="button" className={classes.btnCancel} onClick={onClose}>
                            Cancel
                        </button>
                        <div className={classes.footerRight}>
                            <button type="submit" className={classes.btnSave} disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Account'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}